import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import type {
  SummaryConfig,
  TableConfig,
  TableDataRef,
  TableRowConfig,
} from '@repo/sdk/models/components';

import { equipmentBrowserSummariesByIdsAtom } from '@/features/equipment-browser/equipment-browser-page/state/equipment-browser-summaries.state';

import type { EquipmentBulkEditTableData } from '../equipment-bulk-edit.types';

/**
 * Hook for transforming TableConfig into EquipmentBulkEditTableData.
 * Fetches equipment summaries for device icons via Jotai atom.
 * Setting values are owned by useEquipmentBulkEditForm.
 */
export const useEquipmentBulkEditTable = (table: TableConfig) => {
  // Fetch equipment summaries for device icons
  const equipmentBrowserSummaries = useAtomValue(
    equipmentBrowserSummariesByIdsAtom(table.rows.map((row: TableRowConfig) => row.id))
  );
  // Transform row data into the format expected by the table
  const data = useMemo<EquipmentBulkEditTableData[]>(() => {
    return table.rows.map((row: TableRowConfig): EquipmentBulkEditTableData => {
      const dataRow: EquipmentBulkEditTableData = {};
      dataRow.deviceId = { value: row.id };

      const summary = equipmentBrowserSummaries.find(
        (s: SummaryConfig) => s.referenceId === row.id
      );

      // Transform refs to cell metadata (no values — those live in the form)

      row.refs.forEach((tableRef: TableDataRef): void => {
        dataRow[tableRef.columnId] = {
          actionComponent: tableRef.actionComponent,
          deviceId: row.id,
          displayType: tableRef.displayType,
          displayUnits: tableRef.displayUnits,
          dropdownOptions: tableRef.displayOptions,
          enumOptions: tableRef.enumOptions,
          icon: summary?.deviceIcon,
          units: tableRef.ref?.units,
          value: tableRef.value,
        };
      });

      return dataRow;
    });
  }, [equipmentBrowserSummaries, table.rows]);

  return { data };
};
